// A person's certificates (FR-8.6), on their record and on My space. Links
// only: the files stay in Drive. Rose once expired (a wrong state), amber
// inside the 30-day reminder window (it is waiting on a renewal).
import { useCallback, useEffect, useId, useState, type CSSProperties } from 'react';
import { Badge, Button } from './primitives';
import { PCard } from './people';
import { DisclosedForm } from './card';
import { ConfirmDialog } from './dialog';
import { DateField, Field, TextInput } from './field';
import { InlineStatus, useToast } from './feedback';
import { fmtDate, isoDay, orgToday } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import {
  expiryState,
  listCertificates,
  removeCertificate,
  saveCertificate,
  type Certificate,
} from '../lib/certificates';

const isLink = (v: string) => /^https?:\/\/\S+$/i.test(v.trim());

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--hf-space-2) var(--hf-space-3)',
  padding: 'var(--hf-space-2) 0',
  borderBottom: '1px solid var(--hf-rule)',
  fontSize: 'var(--hf-text-sm)',
};
const NOTE: CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' };
const OPEN: CSSProperties = { color: 'var(--hf-accent-text)', fontSize: 'var(--hf-text-sm)' };

const BLANK = { name: '', issuer: '', issued_on: '', expires_on: '', drive_link: '' };

export function CertificatesCard({
  profileId,
  canEdit,
  label = 'Certificates',
}: {
  profileId: string;
  /** The person themself or people:full; RLS refuses anyone else's write. */
  canEdit: boolean;
  label?: string;
}) {
  const toast = useToast();
  const { tz, dateFormat } = useOrgDates();
  const opener = useId();
  const [rows, setRows] = useState<Certificate[] | null | undefined>(undefined);
  const [editing, setEditing] = useState<Certificate | 'new' | null>(null);
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState<{ name?: string; link?: string; expires?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<Certificate | null>(null);

  const load = useCallback(() => listCertificates(profileId).then(setRows), [profileId]);
  useEffect(() => {
    let live = true;
    void listCertificates(profileId).then((r) => live && setRows(r));
    return () => {
      live = false;
    };
  }, [profileId]);

  const set = (k: keyof typeof BLANK) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const open = (c: Certificate | 'new') => {
    setEditing(c);
    setForm(
      c === 'new'
        ? BLANK
        : {
            name: c.name,
            issuer: c.issuer ?? '',
            issued_on: c.issued_on ?? '',
            expires_on: c.expires_on ?? '',
            drive_link: c.drive_link,
          },
    );
    setErrors({});
    setErr(null);
  };

  const save = async () => {
    const found: typeof errors = {};
    if (!form.name.trim()) found.name = 'Name the certificate.';
    if (!form.drive_link.trim()) found.link = 'Paste the Drive link to the certificate.';
    else if (!isLink(form.drive_link))
      found.link = 'That is not a link. It should start with https://';
    if (form.issued_on && form.expires_on && form.expires_on < form.issued_on)
      found.expires = 'It can’t expire before it was issued.';
    setErrors(found);
    if (found.name || found.link || found.expires) return;
    setBusy(true);
    setErr(null);
    const { error } = await saveCertificate(
      profileId,
      editing === 'new' ? null : (editing?.id ?? null),
      {
        ...form,
        issuer: form.issuer || null,
        issued_on: form.issued_on || null,
        expires_on: form.expires_on || null,
      },
    );
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    toast.push({ tone: 'success', message: `Saved ${form.name.trim()}.` });
    setEditing(null);
    void load();
  };

  const remove = async (c: Certificate) => {
    setRemoving(null);
    const { error } = await removeCertificate(c.id);
    if (error) {
      toast.push({ tone: 'error', message: `Couldn’t remove ${c.name} — ${error.message}` });
      return;
    }
    toast.push({ tone: 'success', message: `Removed ${c.name}.` });
    void load();
  };

  const today = isoDay(orgToday(tz));

  return (
    <PCard
      label={label}
      right={
        canEdit &&
        !editing && (
          <span id={opener}>
            <Button variant="secondary" size="sm" icon="plus" onClick={() => open('new')}>
              Record certificate
            </Button>
          </span>
        )
      }
    >
      {rows === null && (
        <InlineStatus tone="error" action={{ label: 'Retry', onClick: () => void load() }}>
          Couldn’t load the certificates.
        </InlineStatus>
      )}
      {rows && !rows.length && (
        <div style={NOTE}>
          {canEdit ? 'No certificates recorded yet.' : 'None on record that you can see.'}
        </div>
      )}
      {rows?.map((c) => {
        const ex = expiryState(c.expires_on, today);
        return (
          <div key={c.id} role="group" aria-label={c.name} style={ROW}>
            <span style={{ flex: '1 1 12rem', minWidth: 0, overflowWrap: 'anywhere' }}>
              <span style={{ color: 'var(--hf-ink)' }}>{c.name}</span>
              {c.issuer && <span style={NOTE}> · {c.issuer}</span>}
            </span>
            <span style={NOTE}>
              {c.issued_on && `issued ${fmtDate(c.issued_on, dateFormat)}`}
              {c.issued_on && c.expires_on && ' · '}
              {c.expires_on ? `expires ${fmtDate(c.expires_on, dateFormat)}` : 'no expiry'}
            </span>
            {ex.state === 'expired' && (
              <Badge tone="danger" dot>
                expired
              </Badge>
            )}
            {ex.state === 'soon' && (
              <Badge tone="warning" dot>
                {ex.days === 0
                  ? 'expires today'
                  : `expires in ${ex.days} ${ex.days === 1 ? 'day' : 'days'}`}
              </Badge>
            )}
            <a href={c.drive_link} target="_blank" rel="noreferrer noopener" style={OPEN}>
              open
              <span className="hf-sr-only"> {c.name} in Drive</span>
            </a>
            {canEdit && (
              <>
                <Button variant="ghost" size="sm" onClick={() => open(c)}>
                  Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setRemoving(c)}>
                  Remove
                </Button>
              </>
            )}
          </div>
        );
      })}

      {canEdit && editing && (
        <DisclosedForm
          opener={opener}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-3)',
            marginTop: 'var(--hf-space-4)',
          }}
        >
          {err && <InlineStatus tone="error">{err}</InlineStatus>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-3)' }}>
            <Field label="Name" required error={errors.name} style={{ flex: '2 1 14rem' }}>
              <TextInput dense value={form.name} onChange={set('name')} placeholder="First aid" />
            </Field>
            <Field label="Issuer" style={{ flex: '1 1 10rem' }}>
              <TextInput
                dense
                value={form.issuer}
                onChange={set('issuer')}
                placeholder="Red Cross"
              />
            </Field>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-3)' }}>
            <DateField
              dense
              label="Issued"
              value={form.issued_on}
              onChange={set('issued_on')}
              style={{ flex: '1 1 10rem' }}
            />
            <DateField
              dense
              label="Expires"
              hint="Leave empty if it never expires. You and HR are reminded 30 and 7 days before."
              error={errors.expires}
              value={form.expires_on}
              onChange={set('expires_on')}
              style={{ flex: '1 1 10rem' }}
            />
          </div>
          <Field label="Drive link" required error={errors.link}>
            <TextInput
              dense
              type="url"
              value={form.drive_link}
              onChange={set('drive_link')}
              placeholder="https://drive.google.com/…"
            />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void save()}>
              Save certificate
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </DisclosedForm>
      )}

      <ConfirmDialog
        open={!!removing}
        title={`Remove ${removing?.name ?? 'this certificate'}?`}
        consequence="It comes off the record and is no longer reminded. The file in Drive is untouched."
        confirmLabel="Remove certificate"
        tone="danger"
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && void remove(removing)}
      />
    </PCard>
  );
}
